import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
    Table, Spin, Alert, Typography, Select, Row, Col, message, Tooltip, Card, Empty, Tag,
    DatePicker, Statistic, Button, Modal, Space, List, Divider, Collapse, Badge
} from 'antd';
import {
    UserOutlined, TeamOutlined, ScheduleOutlined, MedicineBoxOutlined, WarningOutlined, 
    DownloadOutlined, AuditOutlined, RocketOutlined, EnvironmentOutlined, StarFilled,
    UnorderedListOutlined, CalendarOutlined, CrownOutlined, CheckCircleOutlined 
} from '@ant-design/icons';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { useSelector, useDispatch } from 'react-redux';
import { getMissedVisitsGrouped, exportMissedVisits, getConsecutiveMissedVisits } from '../../services/visitService';
import { getAllDelegatesList } from '../../services/delegateService';
import { fetchCycles } from '../../redux/slices/planning/planningThunks';
import { RoleEnum } from '../../_utils/roleEnum';
import axiosInstance from '../../services/axiosServicesVisit'; 

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Panel } = Collapse;

const MissedVisitsPage = () => {
    const dispatch = useDispatch();
    const isMounted = useRef(true);
    const { user } = useSelector((state) => state.signin);
    const isSupervisor = user?.role?.id === RoleEnum.SUPERVISOR;
    const allCycles = useSelector((state) => state.planning?.cycles || []);

    // --- ÉTATS DONNÉES ---
    const [missedVisitsData, setMissedVisitsData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [consecutiveMissed, setConsecutiveMissed] = useState([]);
    const [consecutiveCycles, setConsecutiveCycles] = useState(2);
    const [loadingConsecutive, setLoadingConsecutive] = useState(false);

    // --- ÉTATS MODALE PARCOURS MÉDECIN ---
    const [doctorModalVisible, setDoctorModalVisible] = useState(false);
    const [doctorDetailData, setDoctorDetailData] = useState([]);
    const [doctorLoading, setDoctorLoading] = useState(false);

    // --- ÉTATS FILTRES CASCADE ---
    const [selectedStartDate, setSelectedStartDate] = useState(dayjs().subtract(1, 'month').startOf('month'));
    const [selectedEndDate, setSelectedEndDate] = useState(dayjs().endOf('month'));
    const [selectedCycleKeys, setSelectedCycleKeys] = useState([]); 
    const [selectedDelegateIds, setSelectedDelegateIds] = useState([]);
    const [selectedDoctorType, setSelectedDoctorType] = useState('all');
    const [selectedIsPartner, setSelectedIsPartner] = useState('all');
    const [selectedPersonId, setSelectedPersonId] = useState('all');
    const [consecutivePersonFilter, setConsecutivePersonFilter] = useState(null);

    const [delegatesList, setDelegatesList] = useState([]);
    const [loadingDelegates, setLoadingDelegates] = useState(false);
    const [exporting, setExporting] = useState(false);

    // 1. REGROUPEMENT DYNAMIQUE DES CYCLES ACTIFS SUR LA PÉRIODE (3 SEMAINES vs 4 SEMAINES)
    const cycleGroups = useMemo(() => {
        if (!allCycles || allCycles.length === 0 || !selectedStartDate || !selectedEndDate) {
            return [];
        }

        const start = selectedStartDate.startOf('day');
        const end = selectedEndDate.endOf('day');

        const activeCycles = allCycles.filter(cycle => {
            if (!cycle.weeks || cycle.weeks.length === 0) return false;
            const sorted = [...cycle.weeks].sort((a, b) => a.week_number - b.week_number);
            const cStart = dayjs(sorted[0].start_date);
            const cEnd = dayjs(sorted[sorted.length - 1].end_date);
            return cStart.isSameOrBefore(end) && cEnd.isSameOrAfter(start);
        });

        const map = new Map();
        activeCycles.forEach(c => {
            const sorted = [...c.weeks].sort((a, b) => a.week_number - b.week_number);
            const duration = sorted.length;
            const pStart = dayjs(sorted[0].start_date).format('DD/MM');
            const pEnd = dayjs(sorted[sorted.length - 1].end_date).format('DD/MM');
            const groupKey = `${c.name.toUpperCase()}__${duration}W`;

            if (!map.has(groupKey)) {
                map.set(groupKey, {
                    key: groupKey,
                    cycleName: c.name.toUpperCase(),
                    duration: duration,
                    periodStr: `du ${pStart} au ${pEnd}`,
                    cycleIds: [],
                    delegates: []
                });
            }

            const item = map.get(groupKey);
            item.cycleIds.push(c.id);
            if (c.delegate) {
                if (!item.delegates.some(d => d.id === c.delegate.id)) {
                    item.delegates.push(c.delegate);
                }
            }
        });

        return Array.from(map.values()).sort((a, b) => a.duration - b.duration || a.cycleName.localeCompare(b.cycleName));
    }, [allCycles, selectedStartDate, selectedEndDate]);

    const cycleSelectOptions = useMemo(() => {
        return cycleGroups.map(g => ({
            value: g.key,
            label: `${g.cycleName} (Délégués ${g.duration} sem - ${g.periodStr})`,
            duration: g.duration
        }));
    }, [cycleGroups]);

    // 2. DÉLÉGUÉS EN CASCADE GROUPÉS PAR TYPE DE CYCLE (4 SEMAINES & 3 SEMAINES)
    const delegateOptionsGrouped = useMemo(() => {
        const activeGroups = selectedCycleKeys.length > 0 
            ? cycleGroups.filter(g => selectedCycleKeys.includes(g.key))
            : cycleGroups;

        const delegates4W = new Map();
        const delegates3W = new Map();

        activeGroups.forEach(g => {
            g.delegates.forEach(d => {
                const label = `${d.firstname || ''} ${d.lastname || ''}`.trim() || `ID:${d.id}`;
                if (g.duration === 4) {
                    delegates4W.set(d.id, { value: String(d.id), label });
                } else {
                    delegates3W.set(d.id, { value: String(d.id), label });
                }
            });
        });

        const optGroups = [];
        if (delegates4W.size > 0) {
            optGroups.push({
                label: `📅 Délégués avec cycle de 4 semaines (${delegates4W.size})`,
                options: Array.from(delegates4W.values())
            });
        }
        if (delegates3W.size > 0) {
            optGroups.push({
                label: `⚡ Délégués avec cycle de 3 semaines (${delegates3W.size})`,
                options: Array.from(delegates3W.values())
            });
        }

        return optGroups;
    }, [cycleGroups, selectedCycleKeys]);

    const doctorTypeOptions = useMemo(() => [
        { value: 'all', label: 'Tous Types Médecin' },
        { value: '1', label: 'Hospitalier' },
        { value: '2', label: 'Privé' },
    ], []);

    const isPartnerOptions = useMemo(() => [
        { value: 'all', label: 'Tous (Partenaire/Standard)' },
        { value: 'true', label: '⭐ Partenaires' },
        { value: 'false', label: 'Standards' },
    ], []);

    const consecutiveDoctorOptions = useMemo(() => {
        if (!consecutiveMissed) return [];
        return [
            { value: null, label: 'Tous les médecins (Récidives)' }, 
            ...consecutiveMissed.map(item => ({ value: item.person?.id, label: item.person?.name }))
        ];
    }, [consecutiveMissed]);

    // 3. TARGETED CYCLE IDS STRING (STABLE, ANTI-BOUCLE)
    const targetedCycleIdsStr = useMemo(() => {
        if (selectedCycleKeys.length === 0) return '';
        const ids = [];
        cycleGroups.forEach(g => {
            if (selectedCycleKeys.includes(g.key)) {
                ids.push(...g.cycleIds);
            }
        });
        return ids.join(',');
    }, [cycleGroups, selectedCycleKeys]);

    const selectedDelegateIdsStr = useMemo(() => {
        return selectedDelegateIds.join(',');
    }, [selectedDelegateIds]);

    const startDateStr = selectedStartDate?.format('YYYY-MM-DD');
    const endDateStr = selectedEndDate?.format('YYYY-MM-DD');

    // Chargement initial
    useEffect(() => {
        isMounted.current = true;
        setLoadingDelegates(true);
        getAllDelegatesList().then(r => { 
            if (!isMounted.current) return;
            setDelegatesList(r?.data?.data || r?.data || []);
        }).finally(() => { if (isMounted.current) setLoadingDelegates(false); });
        dispatch(fetchCycles());
        return () => { isMounted.current = false; };
    }, [dispatch]);

    // ⚡ FIX ANTI-BOUCLE ET DEBOUNCE ⚡
    const debounceTimerRef = useRef(null);

    const loadAllData = useCallback(() => {
        if (!startDateStr || !endDateStr) return;

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        setLoading(true);
        setLoadingConsecutive(true);

        debounceTimerRef.current = setTimeout(() => {
            const commonParams = {
                startDate: startDateStr,
                endDate: endDateStr,
                delegateIds: selectedDelegateIdsStr || undefined,
                cycleIds: targetedCycleIdsStr || undefined,
                doctorType: selectedDoctorType === 'all' ? undefined : parseInt(selectedDoctorType, 10),
                isPartner: selectedIsPartner === 'all' ? undefined : selectedIsPartner,
            };

            // 1. Liste détaillée
            getMissedVisitsGrouped(commonParams)
                .then(res => { if (isMounted.current) setMissedVisitsData(res?.data || []); })
                .catch(() => message.error("Erreur lors de la récupération des visites manquées"))
                .finally(() => { if (isMounted.current) setLoading(false); });

            // 2. Alertes récidives
            getConsecutiveMissedVisits({ ...commonParams, consecutiveCycles })
                .then(res => { if (isMounted.current) setConsecutiveMissed(res.data || []); })
                .catch(() => {})
                .finally(() => { if (isMounted.current) setLoadingConsecutive(false); });
        }, 250);
    }, [startDateStr, endDateStr, selectedDelegateIdsStr, targetedCycleIdsStr, selectedDoctorType, selectedIsPartner, consecutiveCycles]);

    useEffect(() => {
        loadAllData();
        return () => {
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        };
    }, [loadAllData]);

    const handleOpenDoctorHistory = async (personId) => {
        if (!personId) return;
        setDoctorModalVisible(true); setDoctorLoading(true);
        try {
            const res = await axiosInstance.get(`/v1/delegate-stocks/analytics/doctor-full-details`, {
                params: { personId, startDate: '2020-01-01', endDate: dayjs().format('YYYY-MM-DD') }
            });
            setDoctorDetailData(res.data || []);
        } catch (e) { message.error("Erreur historique"); } 
        finally { setDoctorLoading(false); }
    };

    // ⚡ EXPORT CSV DU PARCOURS MÉDECIN AVEC OBSERVATIONS ⚡
    const handleExportDoctorHistory = () => {
        if (doctorDetailData.length === 0) return;
        const docName = doctorDetailData[0].doctorName;
        const headers = ["Date", "Region", "Delegue Visiteur", "Type Visite", "Echantillons", "Produits", "Observations / Note"];
        const rows = doctorDetailData.map(item => {
            const isSupervisor = item.isSupervisor || 
                (item.note && (item.note.includes('[RAPPORT SUPERVISEUR') || item.note.includes('[VISITE DE CONTRÔLE') || item.note.includes('[SUPERVISION'))) ||
                (item.name && item.name.includes('[SUPERVISEUR]'));

            return [
                dayjs(item.reportDate).format('DD/MM/YYYY HH:mm'),
                item.regionName || "N/A",
                item.delegateName,
                isSupervisor ? "Superviseur" : "Délégué",
                item.samples?.map(s => `${s.name} (x${s.quantity})`).join(' | '),
                item.products?.join(' | '),
                `"${(item.note || item.comment || '').replace(/"/g, '""')}"`
            ];
        });
        const csvContent = [headers.join(";"), ...rows.map(e => e.join(";"))].join("\n");
        const blob = new Blob(["\ufeff" + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `Parcours_${docName.replace(/\s+/g, '_')}.csv`;
        link.click();
    };

    // --- COLONNES RÉCIDIVES ---
    const consecutiveColumns = [
        {
            title: 'Médecin en Récidive',
            key: 'name',
            width: 250,
            render: (_, record) => (
                <Space>
                    {record.person?.isPartner && <StarFilled style={{ color: '#fadb14' }} />}
                    <Text strong onClick={() => handleOpenDoctorHistory(record.person.id)} style={{ color: record.person?.isPartner ? '#856404' : '#1890ff', cursor: 'pointer', textDecoration: 'underline' }}>
                        {record.person?.name}
                    </Text>
                </Space>
            )
        },
        { title: 'Spécialité', render: (_, r) => r.person?.specialty || 'N/A', width: 140 },
        { 
            title: 'Délégués concernés', 
            dataIndex: 'missedDelegateDetails', 
            width: 200,
            render: (delegates) => (
                <div>
                    {(delegates || []).map((d, i) => (
                        <div key={i} style={{ marginBottom: 5 }}>
                            <Tag color="orange">{d.delegateName}</Tag>
                            <Badge count={`${d.count} CYCLES`} style={{ backgroundColor: '#f5222d', fontSize: '10px' }} />
                        </div>
                    ))}
                </div>
            )
        },
        { 
            title: 'Détails de la Récidive (Cycles & Durée)', 
            dataIndex: 'missedDelegateDetails', 
            render: (delegates) => (
                <div style={{ maxHeight: '160px', overflowY: 'auto' }}>
                    {(delegates || []).map((d, i) => (
                        <div key={i} style={{ padding: '8px 12px', background: '#fafafa', borderRadius: '6px', marginBottom: '8px', border: '1px solid #f0f0f0' }}>
                            <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: 4 }}>
                                👤 {d.delegateName} :
                            </Text>
                            <List 
                                size="small" 
                                dataSource={d.details} 
                                renderItem={item => (
                                    <List.Item style={{ padding: '2px 0', border: 'none' }}>
                                        <Text style={{ fontSize: '11px' }}>
                                            ⚠️ <b>{item.cycleName}</b> : [{item.period}]
                                        </Text>
                                    </List.Item>
                                )} 
                            />
                        </div>
                    ))}
                </div>
            )
        }
    ];

    // --- COLONNES LISTE DÉTAILLÉE ---
    const missedVisitsColumns = [
        {
            title: 'Médecin Non Visité',
            dataIndex: ['person', 'name'],
            width: 170,
            fixed: 'left',
            render: (name, record) => (
                <Space>
                    {record.person?.isPartner && <StarFilled style={{ color: '#fadb14' }} />}
                    <Text strong onClick={() => handleOpenDoctorHistory(record.person.id)} style={{ color: record.person?.isPartner ? '#856404' : '#1890ff', cursor: 'pointer', textDecoration: 'underline' }}>{name}</Text>
                </Space>
            ),
            sorter: (a, b) => (a.person?.name || '').localeCompare(b.person?.name || ''),
        },
        { title: 'Spécialité', dataIndex: ['person', 'specialtyName'], width: 120 },
        { title: 'Type Médecin', dataIndex: ['person', 'doctorTypeName'], width: 90, render: (text) => <Tag color={text === 'Hospitalier' ? 'blue' : 'green'}>{text}</Tag> },
        { title: 'Région', dataIndex: ['person', 'regionName'], width: 150 },
        {
            title: 'Assignations Manquées (Cycle, Durée & Période Réelle)',
            dataIndex: 'missedAssignments',
            key: 'missedAssignments',
            render: (assignments) => (
                <div style={{ width: '100%' }}>
                    {(assignments || []).map((item, idx) => {
                        const wasVisitedByAnyOther = item.visitedByOthers && item.visitedByOthers.some(o => o.visited);
                        return (
                            <div key={idx} style={{ 
                                padding: '8px 0', 
                                borderBottom: idx !== assignments.length - 1 ? '1px solid #f0f0f0' : 'none',
                                width: '100%' 
                            }}>
                                <Row align="middle" gutter={8}>
                                    <Col flex="150px"><Tag icon={<TeamOutlined />}>{item.delegate.name}</Tag></Col>
                                    <Col flex="160px"><Tag color="geekblue" style={{ fontWeight: 'bold' }}>{item.cycle.name}</Tag></Col>
                                    <Col flex="auto"><Text type="secondary" style={{fontSize: '11px'}}>[{item.cyclePeriod}]</Text></Col>
                                    <Col flex="130px" style={{ textAlign: 'right' }}>
                                        {wasVisitedByAnyOther ? <Tag color="success">Visité autre</Tag> : <Tag color="warning">Non visité autre</Tag>}
                                    </Col>
                                </Row>
                            </div>
                        );
                    })}
                </div>
            )
        },
    ];

    const finalTableData = useMemo(() => {
        if (selectedPersonId === 'all') return missedVisitsData;
        return missedVisitsData.filter(item => String(item.person?.id) === String(selectedPersonId));
    }, [missedVisitsData, selectedPersonId]);

    const kpis = useMemo(() => {
        const totalMissed = finalTableData.length;
        const totalRec = consecutiveMissed.length;
        const concernedDelegateIds = new Set();
        finalTableData.forEach(g => g.missedAssignments?.forEach(a => concernedDelegateIds.add(a.delegate.id)));
        return { totalMissed, totalRecidive: totalRec, totalDelegates: concernedDelegateIds.size };
    }, [finalTableData, consecutiveMissed]);

    const handleExport = async () => {
        setExporting(true);
        try {
            const response = await exportMissedVisits({ 
                startDate: selectedStartDate.format('YYYY-MM-DD'), 
                endDate: selectedEndDate.format('YYYY-MM-DD'), 
                delegateIds: selectedDelegateIdsStr || undefined, 
                cycleIds: targetedCycleIdsStr || undefined,
                isPartner: selectedIsPartner === 'all' ? undefined : selectedIsPartner 
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url; link.setAttribute('download', `export_manquees.csv`);
            document.body.appendChild(link); link.click();
            message.success("Export terminé");
        } catch (e) { message.error("Erreur lors de l'export"); }
        finally { setExporting(false); }
    };

    const isCalculating = loading || loadingConsecutive;

    return (
        <div style={{ padding: '20px' }}>
            <Title level={2}><WarningOutlined style={{ color: '#faad14', marginRight: 12 }} />Analyse des Visites Manquées</Title>

            <Card style={{ marginBottom: 24, background: '#f0f2f5', border: '1px solid #d9d9d9' }}>
                <Row gutter={[16, 16]} align="bottom">
                    {/* 1. CHOIX DE LA PÉRIODE (MOIS OU DATES) */}
                    <Col xs={24} md={6}>
                        <Text strong><CalendarOutlined /> 1. Période calendaire :</Text>
                        <RangePicker 
                            style={{ width: '100%', marginTop: 4 }} 
                            value={[selectedStartDate, selectedEndDate]} 
                            onChange={d => {
                                setLoading(true);
                                setSelectedStartDate(d?.[0]); 
                                setSelectedEndDate(d?.[1]); 
                                setSelectedCycleKeys([]); 
                                setSelectedDelegateIds([]); 
                                setSelectedPersonId('all');
                            }} 
                            format="DD/MM/YYYY" 
                        />
                    </Col>

                    {/* 2. CHOIX DU/DES CYCLES SUR CETTE PÉRIODE (MULTISELECT) */}
                    <Col xs={24} md={8}>
                        <Text strong><ScheduleOutlined /> 2. Cycles actifs (3 sem / 4 sem) :</Text>
                        <Select 
                            mode="multiple" 
                            style={{ width: '100%', marginTop: 4 }} 
                            placeholder={cycleSelectOptions.length > 0 ? "Tous les cycles de la période" : "Aucun cycle trouvé"} 
                            value={selectedCycleKeys} 
                            onChange={(keys) => {
                                setLoading(true);
                                setSelectedCycleKeys(keys);
                                setSelectedDelegateIds([]); 
                            }} 
                            options={cycleSelectOptions} 
                            allowClear
                            maxTagCount="responsive"
                        />
                    </Col>

                    {/* 3. CHOIX DES DÉLÉGUÉS EN CASCADE GROUPÉS (4 SEMAINES & 3 SEMAINES) */}
                    <Col xs={24} md={6}>
                        <Text strong><TeamOutlined /> 3. Délégués concernés :</Text>
                        <Select 
                            mode="multiple" 
                            style={{ width: '100%', marginTop: 4 }} 
                            placeholder="Tous les délégués concernés" 
                            value={selectedDelegateIds} 
                            onChange={(ids) => {
                                setLoading(true);
                                setSelectedDelegateIds(ids);
                            }} 
                            options={delegateOptionsGrouped} 
                            showSearch 
                            optionFilterProp="label" 
                            maxTagCount="responsive"
                            allowClear
                        />
                    </Col>

                    {/* 4. FILTRE PARTENAIRE */}
                    <Col xs={24} md={4}>
                        <Text strong>Partenaire :</Text>
                        <Select 
                            style={{ width: '100%', marginTop: 4 }} 
                            value={selectedIsPartner} 
                            onChange={(v) => {
                                setLoading(true);
                                setSelectedIsPartner(v);
                            }} 
                            options={isPartnerOptions} 
                        />
                    </Col>

                    {/* LIGNE SECONDAIRE */}
                    <Col span={24} style={{ marginTop: 10 }}>
                        <Row gutter={16} align="bottom">
                             <Col span={6}>
                                <Text strong>Type Médecin :</Text>
                                <Select 
                                    style={{ width: '100%', marginTop: 4 }} 
                                    value={selectedDoctorType} 
                                    onChange={(v) => {
                                        setLoading(true);
                                        setSelectedDoctorType(v);
                                    }} 
                                    options={doctorTypeOptions} 
                                />
                             </Col>

                             <Col span={10}>
                                <Text strong>Chercher un médecin dans les résultats :</Text>
                                <Select 
                                    showSearch 
                                    style={{ width: '100%', marginTop: 4 }} 
                                    placeholder="Tous les médecins" 
                                    value={selectedPersonId} 
                                    onChange={setSelectedPersonId} 
                                    options={[{value:'all', label:'Tous les médecins'}, ...missedVisitsData.map(m => ({value: String(m.person.id), label: m.person.name}))]} 
                                    optionFilterProp="label" 
                                />
                             </Col>

                             <Col span={8} style={{ textAlign: 'right' }}>
                                <Button type="primary" icon={<DownloadOutlined />} onClick={handleExport} loading={exporting}>
                                    Exporter CSV
                                </Button>
                             </Col>
                        </Row>
                    </Col>
                </Row>
            </Card>

            {/* ⚡ ZONE DE RÉSULTATS AVEC LOADING GLOBAL ⚡ */}
            <Spin spinning={isCalculating} size="large" tip="Calcul des visites manquées en cours...">
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                    <Col span={8}><Card><Statistic title="Médecins Non Visités" value={kpis.totalMissed} prefix={<UserOutlined />} valueStyle={{ color: '#cf1322' }} /></Card></Col>
                    <Col span={8}><Card><Statistic title="Cas de Récidive" value={kpis.totalRecidive} prefix={<RocketOutlined />} valueStyle={{ color: '#f5222d' }} /></Card></Col>
                    <Col span={8}><Card><Statistic title="Total Délégués Concernés" value={kpis.totalDelegates} prefix={<TeamOutlined />} valueStyle={{ color: '#1890ff' }} /></Card></Col>
                </Row>

                <Collapse expandIconPosition="end" style={{ marginBottom: 24 }}>
                    <Panel 
                        header={
                            <Space>
                                <RocketOutlined style={{ color: 'red' }} />
                                <Text strong style={{ fontSize: '16px' }}>Alertes Récidives (Cycles Successifs)</Text>
                                <Badge count={consecutiveMissed.length} style={{ backgroundColor: '#f5222d' }} />
                            </Space>
                        } 
                        key="1" 
                        style={{ background: '#fff1f0', border: '1px solid #ffa39e', borderRadius: '8px', marginBottom: '16px' }} 
                        extra={
                            <div onClick={e => e.stopPropagation()}>
                                <Space size="large">
                                    <Space>
                                        <Text size="small">Filtrer Médecin :</Text>
                                        <Select 
                                            showSearch 
                                            placeholder="Nom..." 
                                            style={{ width: 220 }} 
                                            size="small" 
                                            optionFilterProp="label" 
                                            value={consecutivePersonFilter} 
                                            onChange={setConsecutivePersonFilter} 
                                            options={consecutiveDoctorOptions} 
                                            allowClear 
                                        />
                                    </Space>
                                    <Space>
                                        <Text size="small">Seuil :</Text>
                                        <Select 
                                            size="small" 
                                            value={consecutiveCycles} 
                                            onChange={(v) => {
                                                setLoadingConsecutive(true);
                                                setConsecutiveCycles(v);
                                            }} 
                                            options={[2,3,4,5].map(v => ({ value: v, label: `${v} cycles` }))} 
                                        />
                                    </Space>
                                </Space>
                            </div>
                        }
                    >
                        <Table 
                            dataSource={consecutivePersonFilter ? consecutiveMissed.filter(item => item.person.id === consecutivePersonFilter) : consecutiveMissed} 
                            columns={consecutiveColumns} 
                            loading={loadingConsecutive} 
                            pagination={{ pageSize: 5 }} 
                            size="small" 
                            bordered 
                        />
                    </Panel>

                    <Panel 
                        header={
                            <Space>
                                <UnorderedListOutlined />
                                <Text strong style={{ fontSize: '16px' }}>Liste détaillée de tous les médecins non visités</Text>
                                <Badge count={finalTableData.length} color="blue" />
                            </Space>
                        } 
                        key="2" 
                        style={{ background: '#e6f7ff', border: '1px solid #91d5ff', borderRadius: '8px' }}
                    >
                        <Table 
                            columns={missedVisitsColumns} 
                            dataSource={finalTableData} 
                            loading={loading} 
                            rowKey={r => r.person.id} 
                            bordered 
                            pagination={{ pageSize: 15 }} 
                            scroll={{ x: 1200 }} 
                        />
                    </Panel>
                </Collapse>
            </Spin>

            {/* ========================================================================= */}
            {/* ⚡ MODALE PARCOURS DE VISITE DU MÉDECIN AVEC DISTINCTION SUPERVISEUR ⚡ */}
            {/* ========================================================================= */}
            <Modal 
                title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '95%', alignItems: 'center' }}>
                        <span><AuditOutlined /> Parcours de visite du Médecin</span>
                        <Button 
                            icon={<DownloadOutlined />} 
                            size="small" 
                            type="primary" 
                            onClick={handleExportDoctorHistory} 
                            disabled={doctorDetailData.length === 0}
                        >
                            Exporter Historique
                        </Button>
                    </div>
                } 
                open={doctorModalVisible} 
                onCancel={() => setDoctorModalVisible(false)} 
                footer={null} 
                width={1150}
            >
                <Spin spinning={doctorLoading}>
                    {/* 1. EN-TÊTE : FICHE RÉCAPITULATIVE DU MÉDECIN */}
                    {doctorDetailData.length > 0 && (
                        (() => {
                            const firstRecord = doctorDetailData[0];
                            const isPartner = firstRecord.isPartner === true || firstRecord.isPartner === 1 || firstRecord.isPartner === 'true';
                            const notes = firstRecord.partnerNotes;

                            // ⚡ 1. FILTRE : Uniquement les cycles oubliés à partir de l'année 2026 ⚡
                            const cyclesCoverage = (firstRecord.cyclesPlanningCoverage || []).map(cov => ({
                                ...cov,
                                unassignedCycles: (cov.unassignedCycles || []).filter(c => {
                                    if (!c.startDate) return false;
                                    return dayjs(c.startDate).year() >= 2026;
                                })
                            }));

                            const delegatesWithUnassigned = cyclesCoverage.filter(cov => (cov.unassignedCycles || []).length > 0);
                            const totalOublisCount = delegatesWithUnassigned.reduce((acc, curr) => acc + (curr.unassignedCycles?.length || 0), 0);

                            return (
                                <div>
                                    <div style={{ 
                                        marginBottom: 16, 
                                        padding: '16px 20px', 
                                        borderRadius: '10px', 
                                        backgroundColor: isPartner ? '#fffbe6' : '#f0f5ff', 
                                        border: isPartner ? '2px solid #fadb14' : '1px solid #adc6ff',
                                        boxShadow: isPartner ? '0 4px 12px rgba(250, 219, 20, 0.15)' : 'none'
                                    }}>
                                        <Row gutter={[16, 16]} align="middle">
                                            <Col span={16}>
                                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '15px' }}>
                                                    <UserOutlined style={{ fontSize: '28px', marginTop: '5px', color: isPartner ? '#856404' : '#1890ff' }} />
                                                    <div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                                            <Title level={3} style={{ margin: 0, color: isPartner ? '#856404' : '#002766' }}>
                                                                {firstRecord.doctorName}
                                                            </Title>
                                                            {isPartner && (
                                                                <Tag color="gold" style={{ 
                                                                    fontSize: '13px', 
                                                                    fontWeight: '900', 
                                                                    border: '1px solid #d4b106', 
                                                                    borderRadius: '4px',
                                                                    padding: '2px 10px',
                                                                    textTransform: 'uppercase'
                                                                }}>
                                                                    <StarFilled style={{ marginRight: '6px' }} /> MÉDECIN PARTENAIRE
                                                                </Tag>
                                                            )}
                                                        </div>

                                                        <Space wrap size={8} style={{ marginTop: '8px' }}>
                                                            <Tag color="blue" style={{ fontWeight: 'bold' }}>{firstRecord.specialtyName}</Tag>
                                                            <Text type="secondary"><EnvironmentOutlined /> {firstRecord.regionName || "N/A"}</Text>
                                                            {firstRecord.address && <Text type="secondary">({firstRecord.address})</Text>}
                                                        </Space>

                                                        {isPartner && notes && (
                                                            <div style={{ 
                                                                marginTop: '15px', 
                                                                padding: '10px 15px', 
                                                                background: 'rgba(255, 255, 255, 0.6)', 
                                                                borderRadius: '6px', 
                                                                borderLeft: '4px solid #fadb14' 
                                                            }}>
                                                                <Text strong style={{ color: '#856404', display: 'block', marginBottom: '3px' }}>
                                                                    Historique Partenariat :
                                                                </Text>
                                                                <Text style={{ color: '#856404', fontSize: '13px' }}>
                                                                    {notes}
                                                                </Text>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col span={8} style={{ 
                                                textAlign: 'right', 
                                                borderLeft: `2px solid ${isPartner ? '#fadb14' : '#adc6ff'}`, 
                                                paddingLeft: '20px' 
                                            }}>
                                                <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginBottom: '8px' }}>
                                                    Délégués assignés au portefeuille :
                                                </Text>
                                                <Space wrap size={[4, 8]}>
                                                    {firstRecord.assignedDelegates?.length > 0 ? (
                                                        firstRecord.assignedDelegates.map((name, idx) => (
                                                            <Tag key={idx} color="cyan" icon={<RocketOutlined />} style={{ borderRadius: '10px' }}>
                                                                {name.toUpperCase()}
                                                            </Tag>
                                                        ))
                                                    ) : (
                                                        <Text type="secondary" italic style={{ fontSize: '11px' }}>Aucun délégué assigné</Text>
                                                    )}
                                                </Space>
                                            </Col>
                                        </Row>
                                    </div>

                                    {/* ⚡ 2. ACCORDÉON DES OUBLIS DE PLANIFICATION PAR CYCLE (>= 2026) ⚡ */}
                                    {cyclesCoverage.length > 0 && (
                                        <div style={{ marginBottom: 16 }}>
                                            <Collapse
                                                defaultActiveKey={delegatesWithUnassigned.length > 0 ? ['audit_oubli'] : []}
                                                style={{ 
                                                    background: delegatesWithUnassigned.length > 0 ? '#fff2f0' : '#f6ffed', 
                                                    border: delegatesWithUnassigned.length > 0 ? '1px solid #ffa39e' : '1px solid #b7eb8f',
                                                    borderRadius: '8px'
                                                }}
                                                items={[
                                                    {
                                                        key: 'audit_oubli',
                                                        label: (
                                                            <Space>
                                                                {delegatesWithUnassigned.length > 0 ? (
                                                                    <>
                                                                        <WarningOutlined style={{ color: '#cf1322', fontSize: '16px' }} />
                                                                        <span style={{ color: '#cf1322', fontWeight: 'bold', fontSize: '13px' }}>
                                                                            Alertes Oublis de Planification par Cycle
                                                                        </span>
                                                                        <Badge count={`${totalOublisCount} cycle(s) oublié(s)`} style={{ backgroundColor: '#cf1322' }} />
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '16px' }} />
                                                                        <span style={{ color: '#389e0d', fontWeight: 'bold', fontSize: '13px' }}>
                                                                            Planification Optimale : Ce médecin est planifié dans 100% des cycles
                                                                        </span>
                                                                        <Tag color="success" style={{ marginLeft: 8 }}>100% Couvert</Tag>
                                                                    </>
                                                                )}
                                                            </Space>
                                                        ),
                                                        children: (
                                                            <div>
                                                                {delegatesWithUnassigned.length > 0 ? (
                                                                    delegatesWithUnassigned.map((cov, idx) => {
                                                                        const unassignedList = cov.unassignedCycles || [];
                                                                        const assignedList = cov.assignedCycles || [];

                                                                        return (
                                                                            <div 
                                                                                key={idx} 
                                                                                style={{ 
                                                                                    padding: '10px 14px', 
                                                                                    background: '#ffffff', 
                                                                                    borderRadius: '6px', 
                                                                                    border: '1px solid #ffccc7',
                                                                                    marginBottom: idx !== delegatesWithUnassigned.length - 1 ? 8 : 0 
                                                                                }}
                                                                            >
                                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                                                                    <Text strong style={{ color: '#1890ff', fontSize: '13px' }}>
                                                                                        👤 Délégué : {cov.delegateName}
                                                                                    </Text>
                                                                                    <Tag color="default" style={{ fontSize: '11px', color: '#595959' }}>
                                                                                        Planifié dans {assignedList.length} autre(s) cycle(s)
                                                                                    </Tag>
                                                                                </div>

                                                                                <div>
                                                                                    <Text strong style={{ fontSize: '12px', color: '#cf1322', display: 'block', marginBottom: 6 }}>
                                                                                        ⚠️ NON ASSIGNÉ dans ces cycles ({unassignedList.length}) :
                                                                                    </Text>
                                                                                    <Space wrap size={[6, 8]}>
                                                                                        {unassignedList.map((c, cIdx) => {
                                                                                            const periodStr = c.startDate && c.endDate 
                                                                                                ? `(${dayjs(c.startDate).format('DD/MM/YY')} au ${dayjs(c.endDate).format('DD/MM/YY')})`
                                                                                                : '';

                                                                                            return (
                                                                                                <Tag 
                                                                                                    key={cIdx} 
                                                                                                    color="error" 
                                                                                                    style={{ 
                                                                                                        fontWeight: 'bold', 
                                                                                                        fontSize: '11px', 
                                                                                                        padding: '3px 10px',
                                                                                                        borderRadius: '4px' 
                                                                                                    }}
                                                                                                >
                                                                                                    ⚠️ {c.cycleName} <span style={{ fontWeight: 'normal', opacity: 0.9, marginLeft: 4 }}>{periodStr}</span>
                                                                                                </Tag>
                                                                                            );
                                                                                        })}
                                                                                    </Space>
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })
                                                                ) : (
                                                                    <Text style={{ color: '#389e0d', fontSize: '12px' }}>
                                                                        Tous les délégués assignés ont bien planifié ce médecin dans l'ensemble de leurs cycles. Aucun oubli détecté.
                                                                    </Text>
                                                                )}
                                                            </div>
                                                        )
                                                    }
                                                ]}
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        })()
                    )}
                    
                    {/* 2. EN-TÊTE DU TABLEAU DES VISITES (AVEC NOUVELLE COLONNE OBSERVATIONS) */}
                    <div style={{ 
                        background: '#f5f5f5', 
                        padding: '10px 15px', 
                        borderRadius: '6px', 
                        marginBottom: '10px', 
                        display: 'flex', 
                        fontWeight: 'bold',
                        fontSize: '12px',
                        border: '1px solid #e8e8e8'
                    }}>
                        <div style={{ width: '15%' }}>Date & Heure</div>
                        <div style={{ width: '20%' }}>Visiteur</div>
                        <div style={{ width: '22%' }}>Échantillons distribués</div>
                        <div style={{ width: '18%' }}>Produits présentés</div>
                        <div style={{ width: '25%' }}>Observations / Notes</div>
                    </div>

                    {/* 3. LISTE DES VISITES AVEC IDENTIFICATION DISTINCTIVE SUPERVISEUR */}
                    <List 
                        dataSource={doctorDetailData} 
                        locale={{ emptyText: "Aucune visite trouvée pour ce médecin." }}
                        renderItem={item => {
                            const isSupervisorVisit = item.isSupervisor || 
                                (item.note && (item.note.includes('[RAPPORT SUPERVISEUR') || item.note.includes('[VISITE DE CONTRÔLE') || item.note.includes('[SUPERVISION'))) ||
                                (item.name && (item.name.includes('[SUPERVISEUR]') || item.name.toLowerCase().includes('visite de contrôle')));

                            let displayNote = item.note || item.comment || '';
                            if (displayNote.includes('Observations :')) {
                                displayNote = displayNote.split('Observations :')[1].trim();
                            }

                            return (
                                <List.Item style={{ 
                                    padding: '12px 15px', 
                                    marginBottom: '8px',
                                    borderRadius: '6px',
                                    // ⚡ FOND VIOLET DOUX ET BORDURE VIOLETTE POUR LES SUPERVISEURS ⚡
                                    background: isSupervisorVisit ? '#fcf6ff' : '#ffffff', 
                                    border: isSupervisorVisit ? '1px solid #d3adf7' : '1px solid #f0f0f0',
                                    borderLeft: isSupervisorVisit ? '5px solid #722ed1' : '1px solid #f0f0f0',
                                    transition: 'all 0.2s'
                                }}>
                                    <div style={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                                        {/* Date & Heure */}
                                        <div style={{ width: '15%' }}>
                                            <Text strong>{dayjs(item.reportDate).format('DD/MM/YYYY')}</Text>
                                            <br/>
                                            <Text type="secondary" style={{ fontSize: '11px' }}>{dayjs(item.reportDate).format('HH:mm')}</Text>
                                        </div>

                                        {/* Visiteur (Badge violet avec couronne 👑 si superviseur) */}
                                        <div style={{ width: '20%' }}>
                                            {isSupervisorVisit ? (
                                                <Tag color="purple" style={{ fontWeight: 'bold', padding: '3px 8px', borderRadius: '4px' }}>
                                                    <CrownOutlined style={{ marginRight: 5 }} /> {item.delegateName}
                                                </Tag>
                                            ) : (
                                                <Tag icon={<UserOutlined />} color="default">
                                                    {item.delegateName}
                                                </Tag>
                                            )}
                                        </div>

                                        {/* Échantillons */}
                                        <div style={{ width: '22%' }}> 
                                            {item.samples && item.samples.length > 0 ? (
                                                item.samples.map((s, i) => (
                                                    <Tag 
                                                        key={i} 
                                                        color={s.isSpecific ? "error" : "processing"} 
                                                        style={{ marginBottom: '3px', fontSize: '11px' }}
                                                    >
                                                        {s.isSpecific && "[HORS CIBLE] "}{s.name} x{s.quantity}
                                                    </Tag>
                                                ))
                                            ) : (
                                                <Text type="secondary" style={{ fontSize: '11px' }}>Aucun</Text>
                                            )}
                                        </div>

                                        {/* Produits */}
                                        <div style={{ width: '18%' }}> 
                                            {item.products && item.products.length > 0 ? (
                                                item.products.map((p, i) => (
                                                    <Tag key={i} color="orange" style={{ marginBottom: '3px', fontSize: '11px' }}>{p}</Tag>
                                                ))
                                            ) : (
                                                <Text type="secondary" style={{ fontSize: '11px' }}>-</Text>
                                            )}
                                        </div>

                                        {/* ⚡ NOUVELLE COLONNE : OBSERVATIONS / NOTES ⚡ */}
                                        <div style={{ width: '25%' }}>
                                            {displayNote ? (
                                                <Tooltip title={item.note || displayNote}>
                                                    <div style={{ 
                                                        fontSize: '12px', 
                                                        color: isSupervisorVisit ? '#531dab' : '#262626', 
                                                        background: isSupervisorVisit ? '#f9f0ff' : '#f5f5f5',
                                                        padding: '5px 10px',
                                                        borderRadius: '4px',
                                                        border: isSupervisorVisit ? '1px dashed #d3adf7' : '1px solid #e8e8e8',
                                                        maxHeight: '48px',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        cursor: 'pointer'
                                                    }}>
                                                        📝 {displayNote}
                                                    </div>
                                                </Tooltip>
                                            ) : (
                                                <Text type="secondary" style={{ fontSize: '11px' }}>-</Text>
                                            )}
                                        </div>
                                    </div>
                                </List.Item>
                            );
                        }} 
                    />
                    {doctorDetailData.length === 0 && !doctorLoading && <Empty description="Aucune visite trouvée pour ce médecin." />}
                </Spin>
            </Modal>
            <style>{`.row-partner-gold td { background-color: #fffbe6 !important; }`}</style>
        </div>
    );
};

export default MissedVisitsPage;